import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { DeliverableBadge } from "@/components/Badges";
import { DELIVERABLE_STATUS_META, formatDate, relativeDue } from "@/lib/constants";
import { Loader2, Users, MapPin, CalendarClock, UserCog, Clock, ExternalLink } from "lucide-react";
import { ClientAvatar } from "@/components/ClientAvatar";
import { parseISO, isBefore, startOfDay } from "date-fns";
import { toast } from "sonner";

function WorkCard({ item, onClick, onDragStart }) {
  const overdue = item.due_date && item.status !== "DELIVERED" && isBefore(parseISO(item.due_date), startOfDay(new Date()));
  const meta = DELIVERABLE_STATUS_META[item.status] || DELIVERABLE_STATUS_META.NEW;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item)}
      onClick={onClick}
      data-testid={`workload-card-${item.deliverable_id}`}
      className={`w-full cursor-pointer rounded-lg border bg-card p-3 text-left transition-transform duration-200 hover:-translate-y-0.5 hover:border-primary/40 ${item.status === "DELIVERED" ? "opacity-60" : ""} ${overdue ? "border-rose-500/40" : "border-border"}`}>
      <div className="flex items-center justify-between gap-2">
        <DeliverableBadge type={item.type} />
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.chip}`}>{meta.label}</span>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-xs font-medium leading-snug">
        <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
        <span className="line-clamp-2">{item.address}</span>
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`workload-client-${item.deliverable_id}`}>
        {item.client_name && <ClientAvatar name={item.client_name} className="h-4 w-4 text-[7px]" />}<span className="truncate">{item.client_name || "—"}</span>
      </div>
      <div title={item.due_date ? formatDate(item.due_date) : "No due date"}
        className={`mt-2 flex items-center gap-1.5 text-sm font-semibold ${overdue ? "text-rose-500 dark:text-rose-400" : "text-foreground"}`}>
        <CalendarClock className="h-3.5 w-3.5" />
        {item.due_date ? relativeDue(item.due_date) : "No due date"}
      </div>
    </div>
  );
}

export default function Workload() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/editors/workload")
    .then(({ data }) => setData(data))
    .catch((err) => toast.error(apiError(err)))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const hasEditors = data.some((b) => b.editor);

  const onDragStart = (e, item) => {
    e.dataTransfer.setData("orderId", item.order_id);
    e.dataTransfer.setData("deliverableId", item.deliverable_id);
    e.dataTransfer.setData("fromEditor", item.editor_id || "");
  };

  const reassign = async (orderId, deliverableId, toEditorId, targetName) => {
    try {
      await api.patch(`/orders/${orderId}/deliverables/${deliverableId}`, { editor_id: toEditorId });
      toast.success(toEditorId ? `Reassigned to ${targetName}` : "Unassigned");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const onDrop = (e, toEditorId, targetName) => {
    e.preventDefault();
    setDragOver(null);
    const orderId = e.dataTransfer.getData("orderId");
    const deliverableId = e.dataTransfer.getData("deliverableId");
    const fromEditor = e.dataTransfer.getData("fromEditor");
    if (orderId && deliverableId && (fromEditor || "") !== (toEditorId || "")) {
      reassign(orderId, deliverableId, toEditorId, targetName);
    }
  };

  return (
    <div>
      <PageHeader title="Editor Workload" subtitle="Balance post-production across your team — drag cards between editors to reassign" testid="workload-header" />
      <div className="p-6 md:p-8">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : !hasEditors ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center" data-testid="workload-empty">
            <Users className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No editors yet. Add editors in Settings, then assign them to deliverables.</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="workload-board">
            {data.map((bucket) => {
              const ed = bucket.editor;
              const editorId = ed?.id || "";
              const color = ed?.color || "#94A3B8";
              const colKey = ed?.id || "unassigned";
              return (
                <div key={colKey} data-testid={`workload-column-${colKey}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(colKey); }}
                  onDragLeave={() => setDragOver((c) => (c === colKey ? null : c))}
                  onDrop={(e) => onDrop(e, editorId, ed?.name)}
                  className={`flex w-[300px] shrink-0 flex-col rounded-xl border bg-card/30 transition-colors ${dragOver === colKey ? "border-primary/60 bg-primary/5" : "border-border"}`}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ background: color }}>
                        {ed ? ed.name.slice(0, 2).toUpperCase() : <UserCog className="h-3.5 w-3.5" />}
                      </span>
                      <div>
                        <div className="text-sm font-semibold leading-none">
                          {ed && ed.link ? (
                            <a href={ed.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" data-testid={`workload-editor-link-${ed.id}`}>
                              {ed.name}<ExternalLink className="h-3 w-3 opacity-70" />
                            </a>
                          ) : (ed ? ed.name : "Unassigned")}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                          <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{bucket.active} active</span>
                        </div>
                      </div>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">{bucket.total}</span>
                  </div>
                  <div className="flex-1 space-y-2.5 overflow-y-auto px-3 pb-3" style={{ maxHeight: "calc(100vh - 240px)" }}>
                    {bucket.items.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border py-8 text-center text-xs text-muted-foreground">Drop products here</div>
                    ) : (
                      bucket.items.map((it) => (
                        <WorkCard key={it.deliverable_id} item={{ ...it, editor_id: editorId }}
                          onDragStart={onDragStart}
                          onClick={() => navigate(`/orders/${it.order_id}`)} />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
